import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MenuConfig, MenuItem } from 'src/app/shared/interfaces/menu-config.interface';
import { formatarTexto } from 'src/app/shared/utils/formatador-de-texto-tipo-um';
import { buscarMenuDoModulo } from './utils/buscar-menu-do-modulo';
import { ArmazenamentoDeBaseService, ArmazenamentoDeUsuarioService } from 'src/app/shared/services/armazenamento';

@Component({
  selector: 'app-side-menu',
  templateUrl: './side-menu.component.html',
  standalone: true,
  imports: [CommonModule, RouterModule],
  styleUrls: ['./side-menu.component.scss'],
})
export class SideMenuComponent implements OnInit {
  @ViewChild('menu') menu: ElementRef = {} as ElementRef;
  @Output() mudarVisualizacaoMenu = new EventEmitter();

  public formatarTexto = formatarTexto;

  public menuConfig: MenuConfig[] = [];

  constructor(
    private renderer: Renderer2,
    private router: Router,
    private route: ActivatedRoute,
    public armazenamentoDeBaseService: ArmazenamentoDeBaseService,
    private armazenamentoDeUsuarioService: ArmazenamentoDeUsuarioService
  ) {
    this.renderer.listen('window', 'click', (e: Event) => {
      if (!this.menu.nativeElement.contains(e.target)) {
        const isColapsado = this.menu.nativeElement.classList.contains(
          'menu-lateral-colapsado'
        );
        isColapsado &&
          window.innerWidth > 800 &&
          this.lidarComItemClick(1000, 1000);
      }
    });
  }

  ngOnInit(): void {
    const usuario = this.armazenamentoDeUsuarioService.buscarUsuario();
    // A função importa só na corretora, onde gestor e supervisor dividem o
    // mesmo módulo e não veem as mesmas coisas.
    this.menuConfig = this.comRotasResolvidas(
      buscarMenuDoModulo(usuario.modulo.id, usuario?.funcao?.id)
    );
  }

  /**
   * Resolve os segmentos de rota de cada item uma única vez.
   *
   * Calcular no template devolvia um array novo a cada ciclo de detecção. O
   * `RouterLink` trata isso como entrada alterada e emite `onChanges`; o
   * `RouterLinkActive` do item pai ouve esses eventos e, como o estado ativo
   * dos irmãos não bate com o dele, agenda um `queueMicrotask` — que mantém a
   * zona ocupada, força outro ciclo e recomeça tudo. Microtarefa não cede a
   * thread: a aba congelava ao abrir qualquer sub-item.
   *
   * A cópia também é o que permite mexer em `isAberto` sem escrever no menu
   * constante, compartilhado por todas as instâncias.
   */
  private comRotasResolvidas(menu: MenuConfig[]): MenuConfig[] {
    return menu.map((particao) => ({
      ...particao,
      items: particao.items.map((item) => ({
        ...item,
        rota: item.subItems
          ? undefined
          : this.segmentosRotaMenu(particao.path, item.path, item.nome),
        subItems: item.subItems?.map((subItem) => ({
          ...subItem,
          rota: this.segmentosRotaSubItem(
            particao.path,
            item.path,
            subItem.path,
            subItem.nome
          ),
        })),
      })),
    }));
  }

  public segmentosRotaMenu(
    particaoPath: string | undefined,
    itemPath: string | undefined,
    nome: string
  ): string[] {
    const segments: string[] = [];
    if (particaoPath) {
      segments.push(particaoPath);
    }
    const destino = itemPath ?? formatarTexto(nome);
    segments.push(...destino.split('/').filter(Boolean));
    return segments;
  }

  public segmentosRotaSubItem(
    particaoPath: string | undefined,
    itemPath: string | undefined,
    subItemPath: string | undefined,
    subItemNome: string
  ): string[] {
    const destino = itemPath
      ? `${itemPath}/${subItemPath ?? formatarTexto(subItemNome)}`
      : (subItemPath ?? formatarTexto(subItemNome));
    return this.segmentosRotaMenu(particaoPath, destino, subItemNome);
  }

  public lidarComItemClick(
    partIndex: number,
    itemIndex: number,
    isAberto?: boolean,
    e?: Event
  ) {
    e?.stopPropagation();
    this.menuConfig.forEach((particao: MenuConfig, particaoIndex: number) => {
      particao.items.forEach((item: MenuItem, partItemIndex: number) => {
        const isItemClicado =
          particaoIndex == partIndex && partItemIndex == itemIndex;
        const isAbertoNovoValor = isAberto
          ? isAberto
          : !this.menuConfig[partIndex]?.items[itemIndex].isAberto;
        this.menuConfig[particaoIndex].items[partItemIndex].isAberto =
          isItemClicado ? isAbertoNovoValor : false;
      });
    });
  }

  public navegarParaEndpoint(segments: string[] | undefined) {
    if (!segments?.length) {
      return;
    }
    this.router.navigate(segments, { relativeTo: this.route });
  }
}
